import { AxiosRequestSender } from '@vue-storefront/sdk-axios-request-sender';
import axios from 'axios';
import { LoggerFactory, LoggerType } from '@vue-storefront/logger';

class Config extends Map {
    constructor() {
        super();
    }
    get(key) {
        return super.get(key);
    }
    set(key, value) {
        super.set(key, value);
        return this;
    }
}
const config = new Config();

/**
 * Resolve base URL based on the client-side or server-side environment.
 * Fallbacks to `apiUrl` when there is no `ssrApiUrl` on server-side environment,
 * bacause `ssrApiUrl` is optional.
 */
const resolveBaseUrl = () => {
    const apiUrl = config.get("apiUrl");
    const ssrApiUrl = config.get("ssrApiUrl");
    const baseUrl = typeof window === "undefined" ? ssrApiUrl || apiUrl : apiUrl;
    return baseUrl;
};

/**
 * This is a proxy that will be used to create an axios instance.
 * It will be used to set the base url and withCredentials flag.
 * Depending on the client-side or server side usage, the base url will be either the `apiUrl` or the `ssrApiUrl`.
 * `resolveBaseUrl` is taking care of that.
 */
const client = new Proxy(axios.create(), {
    get: (axiosInstance, property) => {
        axiosInstance.defaults.withCredentials = true;
        axiosInstance.defaults.baseURL = resolveBaseUrl();
        return axiosInstance[property];
    },
});

/**
 * Method allowing to pass any query to the Bloomreach GraphQL Commerce API.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using query passed via param.
 *
 * You can use this method to fetch information about certain item.
 *
 * @param params - Check the type for more information.
 *
 * @returns
 * Returns an item.
 *
 * @example
 * Passing own custom query fetching categories with only displayName field.
 * The method returns a raw response body. Desired data is scoped to the GraphQL
 * query name.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const query = `#graphql
 * query findCategories($queryHint: QueryHintInput = null) {
 *   findCategories(queryHint: $queryHint) {
 *     displayName
 *   }
 * }`
 * const { findCategories: categories } = await sdk.brd.customQuery({
 *  query
 * });
 * ```
 */
async function customQuery(params) {
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: "customQuery",
            query: params.query,
        },
    ])
        .send();
}

/**
 * Method removing quotes from keys in JSON string.
 */
const $extractInput = ($input) => {
    return $input ? JSON.stringify($input).replace(/"([^"]+)":/g, "$1:") : null;
};
const $inputUndefined = ($input) => {
    return !$input || $input.length === 0;
};

const buildFindSuggestionsQuery = (params) => {
    const { text, queryHint, expect, queryName } = params;
    const fields = expect || "terms";
    return `#graphql
  query ${queryName}(
    $text: String = "${text}",
    $queryHint: QueryHintInput = ${$extractInput(queryHint)}) {
      findSuggestions(text: $text, queryHint: $queryHint) {
        ${fields}
      }
  }`;
};

const fragmentId = "id";
const fragmentImage = `
  dimension {
    w
    h
  }
  link {
    href
    ref
  }
`;
const fragmentImageSet = `
  original { ${fragmentImage} }
  thumbnail { ${fragmentImage} }
`;
const fragmentPrice = `
  moneyAmounts {
    amount
    currency
    displayValue
  }
`;
const fragmentAttrs = `
  name
  values
`;
const fragmentItem = `
  slug
  itemId { ${fragmentId} }
  displayName
  description  
  purchasePrice { ${fragmentPrice} }
  listPrice { ${fragmentPrice} }
  imageSet { ${fragmentImageSet} }
  salePriceRange
  priceRange
  customAttrs { ${fragmentAttrs} }
`;
const fragmentVariant = `
  itemId { ${fragmentId} }
  displayName
  description
  master
  imageSet { ${fragmentImageSet} }
  purchasePrice { ${fragmentPrice} }
  listPrice { ${fragmentPrice} }
  mainItem { 
    itemId { ${fragmentId} }
    displayName
  }
  customAttrs { ${fragmentAttrs} }
  availability {
    inStock
    availableQuantity
    restockableInDays
  }
`;
const fragmentFacets = `
  fields {
    id
    name
    values {
      id
      parentId
      name
      count
    }
  }
`;
const fragmentItemsMeta = `
  total 
  limit
  offset
  facetResult { ${fragmentFacets} }
`;
const queryItem = `
  ${fragmentItem}
  variants { ${fragmentVariant} }`;
const queryItems = ` 
  ${fragmentItemsMeta}
  items { ${queryItem} }
`;

var definitions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  fragmentId: fragmentId,
  fragmentImage: fragmentImage,
  fragmentImageSet: fragmentImageSet,
  fragmentPrice: fragmentPrice,
  fragmentAttrs: fragmentAttrs,
  fragmentItem: fragmentItem,
  fragmentVariant: fragmentVariant,
  fragmentFacets: fragmentFacets,
  fragmentItemsMeta: fragmentItemsMeta,
  queryItem: queryItem,
  queryItems: queryItems
});

const buildFindItemsByCategoryQuery = (params) => {
    const { queryHint, expect, limit, offset, sortFields, categoryId, queryName, } = params;
    const fields = expect || queryItems;
    return `#graphql
  query ${queryName}(
    
      $categoryId: String = "${categoryId}", 
      $limit: Int = ${limit ?? 20}, 
      $offset: Int = ${offset ?? 0}, 
      $sortFields: String = "${sortFields || ""}", 
      $queryHint: QueryHintInput = ${$extractInput(queryHint)}

      ) {
        findItemsByCategory(categoryId: $categoryId, limit: $limit, offset: $offset, queryHint: $queryHint, sortFields: $sortFields) {
          ${fields}
        } 
    }
  `;
};

const errorMessage = {
    API_GENERAL: "Can't get data from Bloomreach Discovery.",
    API_OPERATION_UNDEFINED: "Operation name must be defined.",
    API_QUERY_UNDEFINED: "Query string must be defined.",
    API_OPERATION_NO_ALLOWED: "Operation not allowed.",
    QUERY_CATEGORY_ID: "Category ID was not defined.",
    QUERY_CATEGORY_SLUG: "Category Slug was not defined.",
    QUERY_ITEM_ID: "Item ID was not defined.",
    QUERY_ITEM_KEYWORD: "Keyword was not defined.",
    QUERY_WIDGET_ID: "Define widget ID and at least one search parameter.",
    PARAMS_UNDEFINED: "Helper params were not defined.",
};

const logger = LoggerFactory.create(LoggerType.ConsolaGcp, {
    verbosity: "debug",
});

const buildFindItemsByKeywordQuery = (params) => {
    if (!params)
        return logger.error(errorMessage.PARAMS_UNDEFINED);
    const { queryHint, expect, limit, offset, text, sortFields, queryName } = params;
    const fields = expect || queryItems;
    return `#graphql
    query ${queryName}(
      $text: String = "${text}", 
      $limit: Int = ${limit || 20}, 
      $offset: Int = ${offset || 0}, 
      $sortFields: String = "${sortFields || ""}", 
      $queryHint: QueryHintInput = ${$extractInput(queryHint)}) {
        findItemsByKeyword(text: $text, limit: $limit, offset: $offset, queryHint: $queryHint, sortFields: $sortFields) {
          ${fields}
        }
          
    }
  `;
};

const buildFindItemsByWidgetQuery = (params) => {
    const { text, pids, categoryId, limit, offset, sortFields, queryHint, expect, queryName, } = params;
    const fields = expect || queryItems;
    return `#graphql
  query ${queryName}(
    $text: String = "${text || null}", 
    $pids: String = "${pids || null}", 
	  $categoryId: String = "${categoryId || null}", 
	  $limit: Int = ${limit || 20}, 
	  $offset: Int = ${offset || 0}, 
	  $sortFields: String = "${sortFields || ""}", 
	  $queryHint: QueryHintInput = ${$extractInput(queryHint)}) {
      findItemsByWidget(
        text: $text, 
        pids: $pids, 
        categoryId: $categoryId, 
        limit: $limit, 
        offset: $offset, 
        queryHint: $queryHint, 
        sortFields: $sortFields) {
          ${fields}
        }
	}`;
};

const buildFindCategoriesQuery = (params = {}) => {
    const { queryHint, expect } = params;
    const fields = expect ||
        `
    id
    key
    slug
    displayName
    parentId
    path
  `;
    return `#graphql
  query findCategories($queryHint: QueryHintInput = ${$extractInput(queryHint)}) {
	  findCategories(queryHint: $queryHint) {
      ${fields}
    }
	}`;
};

const buildFindCategoryByIdQuery = (params) => {
    if (!params)
        return logger.error(errorMessage.PARAMS_UNDEFINED);
    const { id, queryHint, expect } = params;
    if ($inputUndefined(id))
        return logger.error(errorMessage.QUERY_CATEGORY_ID);
    const fields = expect ||
        `
    id
    key
    slug
    displayName
    parentId
    path
  `;
    return `#graphql
  query findCategoryById($id: String = "${id}", $queryHint: QueryHintInput = ${$extractInput(queryHint)}) {
    findCategoryById(id: $id, queryHint: $queryHint) {
      ${fields}
    }
	}`;
};

const buildFindCategoryBySlugQuery = (params) => {
    if (!params)
        return logger.error(errorMessage.PARAMS_UNDEFINED);
    const { slug, queryHint, expect } = params;
    if ($inputUndefined(slug))
        return logger.error(errorMessage.QUERY_CATEGORY_SLUG);
    const fields = expect ||
        `
    id
    key
    slug
    displayName
    parentId
    path
  `;
    return `#graphql
  query findCategoryBySlug($id: String = "${slug}", $queryHint: QueryHintInput = ${$extractInput(queryHint)}) {
    findCategoryBySlug(id: $id, queryHint: $queryHint) {
      ${fields}
    }
	}`;
};

const buildFindItemByIdQuery = (params) => {
    if (!params)
        return logger.error(errorMessage.PARAMS_UNDEFINED);
    const { id, queryHint, expect } = params;
    if ($inputUndefined(id))
        return logger.error(errorMessage.QUERY_ITEM_ID);
    const fields = expect || queryItem;
    return `#graphql
  query findItemById($id: String = "${id}", $queryHint: QueryHintInput = ${$extractInput(queryHint)}) {
	  findItemById(id: $id, queryHint: $queryHint) {
      ${fields}
    }
	}`;
};

/**
 * Method finding categories in Bloomreach Discovery.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findCategories } query.
 *
 * You can use this method to fetch information about categories.
 *
 * @param params - Check the type for more information.
 *
 * @returns
 * Returns an array of categories.
 *
 * @example
 * Fetching categories.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const categories = await sdk.brs.findCategories();
 * ```
 *
 * @example
 * Fetching categories with only specified fields.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const categories = await sdk.brs.findCategories({
 *  expect: `
 *    displayName
 *    id
 *  `
 * });
 *
 * @example
 * Fetching categories passing query hint.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const categories = await sdk.brs.findCategories({
 *  queryHint: {
 *    brUid2: 'some value'
 *  }
 * });
 * ```
 */
async function findCategories(params = {}) {
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: "findCategories",
            query: buildFindCategoriesQuery(params),
        },
    ])
        .send();
}

/**
 * Searches for items inside category.
 *
 * @remarks
 * This method comunicates with the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findItemsByCategory } query.
 *
 * @param params - Check the type for more information
 *
 * @returns
 * List of matching items
 *
 * @example
 * Getting items by category Id
 *
 * ```ts
 *   const data = await sdk.brd.findItemsByCategory({
 *     categoryId: 'PNB250864140000'
 *   });
 *   data.items // results
 * ```
 *
 * @example
 * Getting whitelistening what to return
 *
 * ```ts
 *   const data = await sdk.brd.findItemsByCategory({
 *     categoryId: 'PNB250864140000',
 *     expect: `items { displayName }`
 *   });
 *   data.items // items only have `displayName` property
 * ```
 *
 */
async function findItemsByCategory(params) {
    const QUERY_NAME = "findItemsByCategory";
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: QUERY_NAME,
            query: buildFindItemsByCategoryQuery({
                queryName: QUERY_NAME,
                ...params,
            }),
        },
    ])
        .send();
}

/**
 * Search product items by the search term (text) with the pagination input and the optional queryHint (see QueryHintInput for detail)
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findItemsByKeyword } query.
 *
 * @param params - Check the type for more information
 *
 * @returns
 * List of matching items
 *
 * @example
 * Getting products by keyword
 *
 * ```ts
 * const data = await sdk.brd.findItemsByKeyword({
 *   text: 'Greenlee'
 * });
 * data.items // matching products
 * ```
 *
 * @example
 * Getting products by keyword
 *
 * ```ts
 * const data = await sdk.brd.findItemsByKeyword({
 *   text: 'Greenlee',
 *   expect: `items { displayName }`
 * });
 * data.items // matching products
 * ```
 *
 */
async function findItemsByKeyword(params) {
    const QUERY_NAME = "findItemsByKeyword";
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: QUERY_NAME,
            query: buildFindItemsByKeywordQuery({
                queryName: QUERY_NAME,
                ...params,
            }),
        },
    ])
        .send();
}

/**
 * Searches for items by widget. Please note that this request requires tenant config of the
 * bloomreach to have auth key set so that you don't have to provide auth key in the query hint.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findItemsByWidget } query.
 *
 * @param params - Check the type to check specific parameters
 *
 * @returns
 * List of matching items
 *
 * @example
 * Find items by widget.
 *
 * ```ts
 * const results = await sdk.brd.findItemsByWidget({
 *   text: 'bolts',
 *   queryHint: {
 *     widgetId: 'r926zmjv',
 *     widgetType: 'keyword',
 *     brUid2: 'some bruid2'
 *   }
 * });
 * results.items // found items
 * ```
 *
 * @example
 * Find items by widget and pick which fields to return manually
 *
 * ```ts
 * const results = sdk.brd.findItemsByWidget({
 *   text: 'bolts',
 *   queryHint: {
 *     widgetId: 'r926zmjv',
 *     widgetType: 'keyword',
 *     brUid2: 'some bruid2'
 *   },
 *   expect: `
 *     items {
 *       displayName
 *       slug
 *     }
 *   `
 *  });
 *  results.items // found items
 * ```
 *
 */
async function findItemsByWidget(params) {
    const QUERY_NAME = "findItemsByWidget";
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("POST")
        .setProps([
        {
            name: QUERY_NAME,
            query: buildFindItemsByWidgetQuery({
                queryName: QUERY_NAME,
                ...params,
            }),
        },
    ])
        .send();
}

/**
 * Method finding category based on provided ID in Bloomreach Discovery.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findCategoryById } query.
 *
 * You can use this method to fetch information about certain category.
 *
 * @param params - Check the type for more information.
 *
 * @returns
 * Returns a category.
 *
 * @example
 * Fetching category by ID.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const id = 'PNB160400000000';
 * const category = await sdk.brd.findCategoryById({ id });
 * ```
 *
 * @example
 * Fetching category by ID with only specified fields.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const id = 'PNB160400000000';
 * const category = await sdk.brd.findCategoryById({
 *  id,
 *  expect: `
 *    displayName
 *    id
 *  `
 * });
 * ```
 *
 * @example
 * Fetching category by ID passing query hint.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const id = 'PNB160400000000';
 * const category = await sdk.brd.findCategoryById({
 *  id,
 *  queryHint: {
 *    brUid2: 'some value'
 *  }
 * });
 * ```
 */
async function findCategoryById(params) {
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: "findCategoryById",
            query: buildFindCategoryByIdQuery(params),
        },
    ])
        .send();
}

/**
 * Method finding category based on provided slug in Bloomreach Discovery.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findCategoryBySlug } query.
 *
 * You can use this method to fetch information about certain category.
 *
 * @param params - Check the type for more information.
 *
 * @returns
 * Returns a category.
 *
 * @example
 * Fetching category by slug.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const slug = 'some-slug';
 * const category = await sdk.brd.findCategoryBySlug({ slug });
 * ```
 *
 * @example
 * Fetching category by slug with only specified fields.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const slug = 'some-slug';
 * const category = await sdk.brd.findCategoryBySlug({
 *  slug,
 *  expect: `
 *    displayName
 *    slug
 *  `
 * });
 * ```
 *
 * @example
 * Fetching category by slug passing query hint.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const slug = 'some-slug';
 * const category = await sdk.brd.findCategoryBySlug({
 *  slug,
 *  queryHint: {
 *    brUid2: 'some value'
 *  }
 * });
 * ```
 */
async function findCategoryBySlug(params) {
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: "findCategoryBySlug",
            query: buildFindCategoryBySlugQuery(params),
        },
    ])
        .send();
}

/**
 * Method finding item based on provided ID in Bloomreach Discovery.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findItemById } query.
 *
 * You can use this method to fetch information about certain item.
 *
 * @param params - Check the type for more information.
 *
 * @returns
 * Returns an item.
 *
 * @example
 * Fetching item by ID.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const id = '4654916';
 * const item = await sdk.brd.findItemById({ id });
 * ```
 *
 * @example
 * Fetching item by ID with only specified fields.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const id = '4654916';
 * const item = await sdk.brd.findItemById({
 *  id,
 *  expect: `
 *    itemId {
 *     id
 *    }
 *  `
 * });
 * ```
 *
 * @example
 * Fetching item by ID passing query hint.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const id = '4654916';
 * const item = await sdk.brd.findItemById({
 *  id,
 *  queryHint: {
 *    brUid2: 'some value'
 *  }
 * });
 * ```
 */
async function findItemById(params) {
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: "findItemById",
            query: buildFindItemByIdQuery(params),
        },
    ])
        .send();
}

/**
 * Search term and product item suggestions by the term input value (text), with the optional queryHint (see QueryHintInput for detail)
 *
 * @remarks
 * This method sends a GET request to the
 * {@link https://docs.vuestorefrpmt.io/sdk-bloomreach-discovery/reference/api/bloomreach-discovery-api.apisearch | search }
 * endpoint of the Vue Storefront API Middleware. In turn, it receives data from the
 * {@link https://documentation.bloomreach.com/content/reference/graphql-commerce-api | Bloomreach GraphQL Commerce API }
 * using {@link https://graphql-commerce-preprod.bloomreach.io/graphql | findSuggestions } query.
 *
 * @beta This method is in beta and can change in the future.
 *
 * @returns
 * List of matching items
 *
 * @example
 * Find items by widget and pick which fields to return manually
 *
 * ```ts
 * const results = sdk.brd.findItemsByWidget({
 *   text: 'bolts'
 *   `
 *  });
 *  results.items // found items
 * ```
 */
async function findSuggestions(params) {
    const QUERY_NAME = "findSuggestions";
    return new AxiosRequestSender(client)
        .setUrl("search")
        .setMethod("GET")
        .setProps([
        {
            name: "findSuggestions",
            query: buildFindSuggestionsQuery({ queryName: QUERY_NAME, ...params }),
        },
    ])
        .send();
}

var methods = /*#__PURE__*/Object.freeze({
  __proto__: null,
  customQuery: customQuery,
  findCategories: findCategories,
  findItemsByCategory: findItemsByCategory,
  findItemsByKeyword: findItemsByKeyword,
  findItemsByWidget: findItemsByWidget,
  findCategoryById: findCategoryById,
  findCategoryBySlug: findCategoryBySlug,
  findItemById: findItemById,
  findSuggestions: findSuggestions
});

const connector = (options) => {
    config.set("apiUrl", options.apiUrl);
    config.set("ssrApiUrl", options.ssrApiUrl);
    const finalMethods = methods;
    return finalMethods;
};

/**
 * This is the main entry point for the module
 * Factory function that returns the module must be exported
 *
 * @param options - Module options used to configure the module
 *
 * ```ts
 * import { initSDK, buildModule } from '@vue-storefront/sdk';
 * import { bloomreachDiscoveryModule, StripeCTModule } from '@vsf-enterprise/bloomreach-search-gql-sdk';
 *
 * const sdkConfig = {
 *   bloomreachDiscovery: buildModule<BloomreachDiscoveryModule>(bloomreachDiscoveryModule, {
 *     apiUrl: 'http://localhost:9002/brd',
 *     ssrApiUrl: 'http://localhost:9002/brd' // can be used for LAN communication in k8s, e.g. in our infra value would be equal `http://additional-app-middleware:9002/brd`
 *   })
 * }
 *
 * export const sdk = initSDK<typeof sdkConfig>(sdkConfig);
 * ```
 */
const bloomreachDiscoveryModule = (options) => {
    return {
        connector: connector(options),
        subscribers: {},
    };
};

export { bloomreachDiscoveryModule, definitions };
//# sourceMappingURL=index.es.js.map
